import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { useCheckout } from '../CheckoutContext';
import { formatCallSlot, hoursForDay, loadCallWindow, saveCallWindow } from '../lib/callSlots';
import { SolanaPayButton } from './SolanaPay';

/**
 * OpenBro V3. One-screen 1920×1080 reproduction of the desert reference.
 * Desktop fills the viewport. Mobile scrolls.
 */

const DESKTOP_MIN = 1100;

const OFFER = {
  originalPrice: 99,
  currentPrice: 49,
  monthlyCapacity: 200,
  placesAtMonthStart: 80,
  placesAtMonthEnd: 12,
};

const discountPercent = Math.floor(((OFFER.originalPrice - OFFER.currentPrice) / OFFER.originalPrice) * 100);
function offerSnapshot(now = new Date()) {
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const progress = Math.min(1, ((now.getDate() - 1) + now.getHours() / 24) / daysInMonth);
  const raw = OFFER.placesAtMonthStart + (OFFER.placesAtMonthEnd - OFFER.placesAtMonthStart) * progress;
  const places = Math.max(OFFER.placesAtMonthEnd, Math.min(OFFER.placesAtMonthStart, Math.round(raw)));
  const taken = Math.round(((OFFER.monthlyCapacity - places) / OFFER.monthlyCapacity) * 100);
  return { places, taken };
}

const COPY = {
  fr: {
    lang: 'FR',
    nav: ['Discipline', 'Formation', 'Mentorat', 'Communauté', 'Résultats'],
    title: 'LAISSE-MOI\nT’AIDER.',
    lead: 'Business, argent, relations, discipline, sport ou vie perso.',
    punch: [
      ['Parle-m’en.', true],
      [' Tu repars avec un ', false],
      ['plan alimentaire', true],
      [', un ', false],
      ['plan d’entraînement', true],
      [' et un ', false],
      ['plan business', true],
      ['.', false],
    ],
    points: [
      ['Parle-moi directement', 'Un appel privé chaque mois pour faire le point sur ta situation.'],
      ['Repars avec tes plans', 'Alimentaire, entraînement, business. Les 30 prochains jours, déjà écrits.'],
      ['Accède à tout', 'Programmes, communauté privée et nouveaux contenus.'],
    ],
    members: '+10 000 membres ambitieux.',
    membersSub: '',
    cta: 'Réserve ton appel',
    launch: 'Offre de lancement',
    spots: (count) => `Plus que ${count} places ce mois-ci`,
    trust: [
      ['lock', 'Paiement sécurisé'],
      ['cal', 'Résiliable à tout moment'],
      ['bolt', 'Accès immédiat'],
    ],
    live: 'Mentorat privé · en tête-à-tête',
    callTitle: 'Des appels privés\navec moi.',
    callBody: 'Chaque mois, on fait le point en tête-à-tête pour lever tes blocages, ajuster ta stratégie et t’assurer d’avancer.',
    callCta: (time) => `Réserver ${time}`,
    callCtaFallback: 'Réserver ton appel',
    slotOpen: 'Créneau ouvert',
    slotNote: 'confirmé dès le paiement',
    slotPick: 'Choisis ton heure',
    otherDate: 'Autre date',
    otherHours: 'Choisis l’heure',
    packTitle: 'Tout est dans le même abonnement.',
    packBody: 'L’appel privé, les formations et la communauté. Ce n’est pas l’un ou l’autre.',
    packNote: 'Inclus avec l’appel et la communauté.',
    progTitle: 'Des programmes\nde formation exclusifs.',
    progLink: 'Commencer les programmes',
    progBody: 'Des modules concrets et actionnables pour progresser dans tous les domaines de ta vie.',
    programs: [
      { src: '/formations/ig-sport-food.jpg?v=4', name: 'Sport et nutrition', focus: 'center' },
      { src: '/formations/ig-content.mp4', name: 'Contenu', video: true, focus: 'center 36%' },
      { src: '/formations/ig-jobs.jpg?v=3', name: 'Jobs secrets', focus: 'center', secret: true },
      { src: '/formations/ig-relations.jpg', name: 'Relations', focus: 'center 55%' },
      { src: '/formations/ig-mentor.jpg', name: 'Mentor & amis', focus: 'center 28%' },
      {
        name: 'IA',
        tiles: [
          { src: '/formations/tile-startup.jpg', name: 'Startup', focus: 'center' },
          { src: '/formations/tile-saas.jpg', name: 'SaaS', focus: 'center' },
          { src: '/formations/tile-invest.jpg', name: 'Invest', focus: 'center' },
        ],
      },
    ],
    only: 'SEULEMENT',
    price: '49€',
    period: '/mois',
    cancel: 'Résiliable\nà tout moment',
    perks: [
      'Appels privés en tête-à-tête, chaque mois',
      'Tous les programmes de formation',
      'Gaut en privé, dispo 24/7',
      'Plans alimentaire, entraînement et business',
      'Suivi et bilans mensuels',
      'Accès à la communauté privée',
      'Accessible sur mobile et ordinateur',
      'Mise à jour tous les jours à 6h',
    ],
  },
  en: {
    lang: 'EN',
    nav: ['Discipline', 'Training', 'Mentorship', 'Community', 'Results'],
    title: 'LET ME\nHELP YOU.',
    lead: 'Business, money, relationships, discipline, sport or personal life.',
    punch: [
      ['Talk to me.', true],
      [' You leave with a ', false],
      ['meal plan', true],
      [', a ', false],
      ['training plan', true],
      [' and a ', false],
      ['business plan', true],
      ['.', false],
    ],
    points: [
      ['Talk to me directly', 'A private call each month to look at your situation.'],
      ['Leave with your plans', 'Food, training, business. The next 30 days, already written.'],
      ['Get everything', 'Programs, private community and new content.'],
    ],
    members: '+10,000 ambitious members.',
    membersSub: '',
    cta: 'Book your call',
    launch: 'Launch offer',
    spots: (count) => `Only ${count} spots left this month`,
    trust: [
      ['lock', 'Secure payment'],
      ['cal', 'Cancel anytime'],
      ['bolt', 'Instant access'],
    ],
    live: 'Private mentorship · face to face',
    callTitle: 'Private calls\nwith me.',
    callBody: 'Every month we sit down face to face, clear what’s blocking you, adjust the plan, and make sure you move.',
    callCta: (time) => `Book ${time}`,
    callCtaFallback: 'Book your call',
    slotOpen: 'Open slot',
    slotNote: 'confirmed as soon as you pay',
    slotPick: 'Pick a time',
    otherDate: 'Another date',
    otherHours: 'Pick a time',
    packTitle: 'It’s all one subscription.',
    packBody: 'The private call, the programs and the community. Not one or the other.',
    packNote: 'Included with the call and the community.',
    progTitle: 'Exclusive\ntraining programs.',
    progLink: 'Start the programs',
    progBody: 'Concrete modules you can use, across every part of your life.',
    programs: [
      { src: '/formations/ig-sport-food.jpg?v=4', name: 'Sport & nutrition', focus: 'center' },
      { src: '/formations/ig-content.mp4', name: 'Content', video: true, focus: 'center 36%' },
      { src: '/formations/ig-jobs.jpg?v=3', name: 'Secret jobs', focus: 'center', secret: true },
      { src: '/formations/ig-relations.jpg', name: 'Relationships', focus: 'center 55%' },
      { src: '/formations/ig-mentor.jpg', name: 'Mentor & friends', focus: 'center 28%' },
      {
        name: 'AI',
        tiles: [
          { src: '/formations/tile-startup.jpg', name: 'Startup', focus: 'center' },
          { src: '/formations/tile-saas.jpg', name: 'SaaS', focus: 'center' },
          { src: '/formations/tile-invest.jpg', name: 'Invest', focus: 'center' },
        ],
      },
    ],
    only: 'ONLY',
    price: '49€',
    period: '/month',
    cancel: 'Cancel\nanytime',
    perks: [
      'A private face-to-face call each month',
      'Every training program',
      'Gaut in private, available 24/7',
      'Meal, training and business plans',
      'Monthly reviews',
      'Private community access',
      'Phone and computer',
      'New content every day at 6:00',
    ],
  },
};

function PointIcon({ index }) {
  const marks = [
    // Réponses sans filtre : bulle + éclair
    <>
      <path key="bubble" d="M20 13.5a2.5 2.5 0 0 1-2.5 2.5H9.2L4 20V6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5z" />
      <path key="bolt" d="M12.8 6.8 10.2 10.6h3.4l-2.4 3.6" />
    </>,
    // Plan d'action : cible
    <>
      <circle key="t1" cx="12" cy="12" r="8.5" />
      <circle key="t2" cx="12" cy="12" r="4.8" />
      <circle key="t3" cx="12" cy="12" r="1.3" fill="currentColor" />
    </>,
    // Suivi mensuel : courbe qui monte
    <>
      <path key="axis" d="M4 20h16" />
      <path key="trend" d="m4.5 15.5 4.5-4.5 3.5 3.5L19 8" />
      <path key="arrow" d="M14.5 8H19v4.5" />
    </>,
    // Communauté : groupe
    <>
      <circle key="h1" cx="9" cy="8" r="3.4" />
      <path key="b1" d="M3 20v-1.2A4.8 4.8 0 0 1 7.8 14h2.4a4.8 4.8 0 0 1 4.8 4.8V20" />
      <path key="h2" d="M15.5 4.8a3.4 3.4 0 0 1 0 6.4" />
      <path key="b2" d="M18 14.3a4.8 4.8 0 0 1 3 4.5V20" />
    </>,
  ];
  return (
    <span className="v3-ico">
      <svg viewBox="0 0 24 24" aria-hidden="true">{marks[index]}</svg>
    </span>
  );
}

function PayMarks({ lang }) {
  return (
    <span className="v3-pay-marks">
      <span className="v3-pay-apple">
        <svg viewBox="0 0 14 16"><path d="M11.2 8.4c0-1.8 1.5-2.7 1.6-2.8-0.9-1.3-2.2-1.4-2.7-1.5-1.1-0.1-2.2.7-2.8.7s-1.5-.7-2.4-.6c-1.2.1-2.4.7-3 1.9-1.3 2.2-.3 5.5.9 7.3.6.9 1.3 1.9 2.3 1.8.9 0 1.2-.6 2.3-.6s1.4.6 2.4.6 1.6-.9 2.2-1.8c.7-1 1-2 1-2.1-.1 0-1.8-.7-1.8-2.9zM9.4 2.8c.5-.6.8-1.4.7-2.3-.7 0-1.6.5-2.1 1.1-.5.5-.9 1.4-.8 2.2.8.1 1.6-.4 2.2-1z" /></svg>
        Pay
      </span>
      <span>VISA</span>
      <span className="v3-pay-mc" title="Mastercard">
        <i /><i />
      </span>
      <span>CB</span>
      <SolanaPayButton lang={lang} className="v3-pay-sol" />
    </span>
  );
}

function TrustIcon({ name }) {
  const paths = {
    lock: 'M8 11V8a4 4 0 0 1 8 0v3M7 11h10v8H7z',
    cal: 'M7 6h10v12H7zM7 10h10M10 4v4M14 4v4',
    bolt: 'M13 3 6 13h5l-1 8 8-12h-5l0-6z',
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d={paths[name]} />
    </svg>
  );
}

const CALENDAR_DAYS = { fr: ['L', 'M', 'M', 'J', 'V', 'S', 'D'], en: ['M', 'T', 'W', 'T', 'F', 'S', 'S'] };

function SlotCalendar({ lang, cursor, day, hoursLabel, onCursor, onDay, onTime }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const pad = (first.getDay() + 6) % 7;
  const count = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(pad).fill(null), ...Array.from({ length: count }, (_, index) => new Date(year, month, index + 1))];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const latest = new Date(today);
  latest.setDate(today.getDate() + 21);
  const locale = lang === 'fr' ? 'fr-FR' : 'en-GB';
  const monthLabel = cursor.toLocaleDateString(locale, { month: 'long' });
  const hours = (day ? hoursForDay(day) : []).filter((minutes) => {
    if (!day) return false;
    const slot = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, minutes);
    return slot.getTime() >= Date.now() + 90 * 60 * 1000;
  });
  return (
    <div className="v3-cal-pop" onClick={(event) => event.stopPropagation()}>
      <div className="v3-cal-nav">
        <button type="button" disabled={new Date(year, month, 1) <= today} onClick={(event) => { event.stopPropagation(); onCursor(new Date(year, month - 1, 1)); }}>‹</button>
        <strong>{monthLabel}</strong>
        <button type="button" disabled={new Date(year, month + 1, 1) > latest} onClick={(event) => { event.stopPropagation(); onCursor(new Date(year, month + 1, 1)); }}>›</button>
      </div>
      <div className="v3-cal-grid">
        {CALENDAR_DAYS[lang === 'fr' ? 'fr' : 'en'].map((label, index) => <span key={`${label}${index}`}>{label}</span>)}
        {cells.map((date, index) => {
          if (!date) return <i key={`empty-${index}`} />;
          const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          const enabled = start >= today && start <= latest;
          const selected = day && start.getTime() === new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
          return (
            <button key={date.toISOString()} type="button" disabled={!enabled} className={selected ? 'is-on' : ''} onClick={(event) => onDay(date, event)}>
              {date.getDate()}
            </button>
          );
        })}
      </div>
      {day ? (
        <div className="v3-cal-hours">
          <p>{hoursLabel}</p>
          <div>
            {hours.map((minutes) => {
              const hour = String(Math.floor(minutes / 60)).padStart(2, '0');
              const minute = String(minutes % 60).padStart(2, '0');
              const label = lang === 'fr' ? `${hour}h${minute}` : `${hour}:${minute}`;
              return <button key={minutes} type="button" onClick={(event) => onTime(day, minutes, event)}>{label}</button>;
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function OpenBroV3() {
  const { lang, toggleLang } = useLang();
  const { startCheckout, isActive } = useCheckout();
  const t = COPY[lang] || COPY.fr;
  const [callWindow, setCallWindow] = useState(null);
  const slots = (callWindow?.slots || []).map((iso) => formatCallSlot(iso, lang));
  const slotsSpanDays = new Set(slots.map((slot) => slot.day)).size > 1;
  const selectedSlot = slots.find((slot) => slot.iso === callWindow?.selected) || slots[0] || null;
  const pay = () => startCheckout('v3-cta');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarCursor, setCalendarCursor] = useState(() => new Date());
  const [calendarDay, setCalendarDay] = useState(null);
  const bookCall = (event) => {
    if (event?.target?.closest?.('.v3-times, .v3-cal')) return;
    event?.stopPropagation?.();
    if (!selectedSlot) {
      startCheckout('v3-call');
      return;
    }
    startCheckout('v3-call', {
      callAt: selectedSlot.iso,
      callLabel: selectedSlot.label,
      callTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };
  const pickCustomSlot = (day, minutes, event) => {
    event.stopPropagation();
    const slot = new Date(day);
    slot.setHours(0, 0, 0, 0);
    slot.setMinutes(minutes);
    if (slot.getTime() < Date.now() + 90 * 60 * 1000) return;
    const iso = slot.toISOString();
    setCallWindow((current) => {
      if (!current) return current;
      const next = {
        ...current,
        slots: current.slots.includes(iso) ? current.slots : [...current.slots, iso],
        selected: iso,
      };
      saveCallWindow(next);
      return next;
    });
    setCalendarOpen(false);
  };
  const chooseSlot = (iso, event) => {
    event.stopPropagation();
    setCallWindow((current) => {
      if (!current) return current;
      const next = { ...current, selected: iso };
      saveCallWindow(next);
      return next;
    });
  };
  const payHere = (event) => {
    event.stopPropagation();
    pay();
  };
  const payKey = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      pay();
    }
  };
  const boardRef = useRef(null);
  const [meterOn, setMeterOn] = useState(false);
  const { places, taken } = offerSnapshot();

  useEffect(() => {
    const board = boardRef.current;
    const frame = board?.parentElement;
    if (!board || !frame) return undefined;
    const previousBody = document.body.style.overflow;
    const previousHtml = document.documentElement.style.overflow;

    const fit = () => {
      const desktop = frame.clientWidth >= DESKTOP_MIN;
      frame.classList.toggle('is-desktop', desktop);
      if (!desktop) {
        board.style.transform = 'none';
        document.body.style.overflow = previousBody;
        document.documentElement.style.overflow = previousHtml;
        return;
      }
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      board.style.transform = 'none';
    };

    setCallWindow(loadCallWindow());
    fit();
    const frameId = requestAnimationFrame(() => setMeterOn(true));
    const observer = new ResizeObserver(fit);
    observer.observe(frame);
    window.addEventListener('resize', fit);
    document.fonts?.ready.then(fit);
    return () => {
      document.body.style.overflow = previousBody;
      document.documentElement.style.overflow = previousHtml;
      cancelAnimationFrame(frameId);
      observer.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, []);

  return (
    <div className="v3-fit">
      <div className="v3" ref={boardRef}>
        <header className="v3-top">
          <div className="v3-logo">OpenBro<span>✦</span></div>
          <nav>
            {t.nav.map((item) => (
              <button key={item} type="button" className="v3-link" disabled={isActive} onClick={pay}>{item}</button>
            ))}
          </nav>
          <div className="v3-top-actions">
            <button type="button" className="v3-lang" onClick={toggleLang}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" /><path d="M3.5 12h17M12 3.5c2.2 2.4 2.2 14.6 0 17M12 3.5c-2.2 2.4-2.2 14.6 0 17" /></svg>
              {t.lang}
            </button>
            <span className="v3-ring">
              <button type="button" className="v3-top-cta" disabled={isActive} onClick={payHere}>{t.cta} →</button>
            </span>
          </div>
        </header>

        <div className="v3-stage">
          <section className="v3-copy">
            <div>
              <h1>{t.title}</h1>
              <p className="v3-lead">{t.lead}</p>
              <p className="v3-punch">
                {t.punch.map(([text, strong]) => (strong ? <strong key={text}>{text}</strong> : <span key={text}>{text}</span>))}
              </p>
              <ul className="v3-points">
                {t.points.map(([title, text], index) => (
                  <li key={title}>
                    <PointIcon index={index} />
                    <div>
                      <strong>{title}</strong>
                      <span>{text}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="v3-bottom">
              <div className="v3-social">
                <div className="v3-faces" aria-hidden="true">
                  {[1, 2, 3, 4, 5].map((id) => <img key={id} src={`/avatars/young-${id}.jpg`} alt="" />)}
                </div>
                <div>
                  <b>★★★★★</b>
                  <strong>{t.members}</strong>
                </div>
              </div>
              <span className="v3-ring v3-ring-wide">
                <button type="button" className="v3-cta" disabled={isActive} onClick={payHere}>{isActive ? '...' : t.cta} →</button>
              </span>
              <ul className="v3-trust">
                {t.trust.map(([icon, label], index) => (
                  <li key={label} className={index === 0 ? 'v3-pay' : undefined}>
                    <TrustIcon name={icon} />
                    {label}
                    {index === 0 ? <PayMarks lang={lang} /> : null}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <aside className="v3-side">
            <p className="v3-pack">
              <strong>{t.packTitle}</strong>
              <span>{t.packBody}</span>
            </p>
            <article
              className={`v3-card v3-call v3-aim${calendarOpen ? ' is-open' : ''}`}
              onClick={bookCall}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  bookCall(event);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div>
                <p className="v3-live">{t.live}</p>
                <h2>{t.callTitle}</h2>
                <p className="v3-pitch">{t.callBody}</p>
                <div className="v3-slot">
                  <p className="v3-slot-kicker">{t.slotOpen}</p>
                  <p className="v3-slot-day">{selectedSlot ? `${selectedSlot.day} · ${t.slotNote}` : t.slotNote}</p>
                  <div className="v3-times" role="radiogroup" aria-label={t.slotPick}>
                    {slots.map((slot) => (
                      <button
                        key={slot.iso}
                        type="button"
                        role="radio"
                        aria-checked={selectedSlot?.iso === slot.iso}
                        className={selectedSlot?.iso === slot.iso ? 'is-on' : ''}
                        onClick={(event) => chooseSlot(slot.iso, event)}
                      >
                        {slotsSpanDays ? slot.chip : slot.time}
                      </button>
                    ))}
                  </div>
                  <div className="v3-cal">
                    <button
                      type="button"
                      className="v3-cal-open"
                      onClick={(event) => {
                        event.stopPropagation();
                        setCalendarDay(null);
                        setCalendarCursor(new Date());
                        setCalendarOpen((open) => !open);
                      }}
                    >
                      {t.otherDate}
                    </button>
                    {calendarOpen ? (
                      <SlotCalendar
                        lang={lang}
                        cursor={calendarCursor}
                        day={calendarDay}
                        hoursLabel={t.otherHours}
                        onCursor={setCalendarCursor}
                        onDay={(day, event) => {
                          event.stopPropagation();
                          setCalendarDay(day);
                        }}
                        onTime={pickCustomSlot}
                      />
                    ) : null}
                  </div>
                </div>
                <span className="v3-ring">
                  <button type="button" onClick={bookCall}>{selectedSlot ? t.callCta(selectedSlot.time) : t.callCtaFallback} →</button>
                </span>
              </div>
              <div className="v3-portrait">
                <img src="/v3-call.jpg" alt="" />
                <div className="v3-call-ui" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M4 8h4l1.5-2h5L16 8h4v10H4V8zm8 8.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4z" /></svg>
                  <svg viewBox="0 0 24 24"><path d="M12 14a3 3 0 0 0 3-3V7a3 3 0 0 0-6 0v4a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.9V20H9v2h6v-2h-2v-2.1A7 7 0 0 0 19 11h-2z" /></svg>
                  <svg viewBox="0 0 24 24"><path d="M7 4h3l1 4-2 1a12 12 0 0 0 6 6l1-2 4 1v3a2 2 0 0 1-2 2A16 16 0 0 1 5 6a2 2 0 0 1 2-2z" /></svg>
                </div>
              </div>
            </article>

            <article className="v3-card v3-prog" onClick={pay} onKeyDown={payKey} role="button" tabIndex={0}>
              <div className="v3-prog-head">
                <div>
                  <p className="v3-pack-note">{t.packNote}</p>
                  <h2>{t.progTitle}</h2>
                </div>
                <button type="button" className="v3-link" disabled={isActive} onClick={payHere}>{t.progLink} →</button>
              </div>
              <p>{t.progBody}</p>
              <ul className="v3-thumbs">
                {t.programs.map((program) => (
                  <li key={program.name} className={[program.tiles ? 'v3-thumb-mosaic' : '', program.secret ? 'v3-thumb-secret' : ''].filter(Boolean).join(' ') || undefined}>
                    {program.tiles ? (
                      <span className="v3-mosaic">
                        {program.tiles.map((tile) => (
                          <span key={tile.name}>
                            <img src={tile.src} alt="" style={{ objectPosition: tile.focus }} />
                            <em>{tile.name}</em>
                          </span>
                        ))}
                      </span>
                    ) : program.video ? (
                      <video src={program.src} muted autoPlay loop playsInline preload="metadata" style={{ objectPosition: program.focus }} />
                    ) : (
                      <img src={program.src} alt="" style={{ objectPosition: program.focus }} />
                    )}
                    <b>{program.name}</b>
                    <span className="v3-thumb-go" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg>
                    </span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="v3-card v3-price v3-aim" onClick={pay}>
              <div className="v3-offer">
                <span className="v3-launch">{t.launch}</span>
                <span className="v3-spots">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" /><path d="M12 8v5l3 2" /></svg>
                  {t.spots(places)}
                </span>
                <span className="v3-meter" aria-hidden="true"><i style={{ width: meterOn ? `${taken}%` : '0%' }} /></span>
              </div>
              <div className="v3-price-top">
                <div>
                  <p>{t.only}</p>
                  <div className="v3-price-line">
                    <strong>{OFFER.currentPrice}€</strong><em>{t.period}</em>
                    <s>{OFFER.originalPrice}€</s>
                    <b>-{discountPercent}%</b>
                  </div>
                </div>
                <span className="v3-cancel">
                  <i aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="M13 3 6 13h5l-1 8 8-12h-5l0-6z" /></svg>
                  </i>
                  {t.cancel}
                </span>
              </div>
              <ul>
                {t.perks.map((perk) => <li key={perk}>{perk}</li>)}
              </ul>
              <button type="button" className="v3-price-cta" disabled={isActive} onClick={payHere}>{isActive ? '...' : `${t.cta} →`}</button>
              <SolanaPayButton lang={lang} className="v3-sol-mobile" label={lang === 'fr' ? 'Payer en Solana' : 'Pay with Solana'} />
            </article>
          </aside>
        </div>
      </div>
      <style>{`
        .v3-fit { position: fixed; inset: 0; overflow: hidden; background: #1a120c url(/v3-board.jpg) center / cover no-repeat; }
        .v3-fit.is-desktop .v3 { position: absolute; inset: 0; left: 0; top: 0; width: 100%; height: 100%; transform: none; }
        .v3 { position: relative; width: 100%; min-height: 100vh; box-sizing: border-box; display: flex; flex-direction: column; padding: 28px 22px 36px; color: #111; background: #c4a882 url(/v3-board.jpg) center center / cover no-repeat; font-family: Inter, Helvetica Neue, Arial, sans-serif; overflow: hidden; }
        .v3-fit.is-desktop .v3 { padding: 32px 52px 40px; transform-origin: center center; }
        .v3-top { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 24px; flex: none; }
        .v3-logo { font-family: "Inter Tight", Inter, sans-serif; font-weight: 800; font-size: 28px; letter-spacing: -0.045em; color: #fff; }
        .v3-fit.is-desktop .v3-logo { font-size: 34px; }
        .v3-logo span { font-size: 0.48em; margin-left: 1px; }
        .v3-link { position: relative; background: none; color: inherit; font: inherit; letter-spacing: inherit; text-transform: inherit; padding: 0; cursor: pointer; }
        .v3-link::after { content: ""; position: absolute; left: 0; right: 0; bottom: -3px; height: 1px; background: currentColor; transform: scaleX(0); transform-origin: left center; transition: transform 0.28s ease; }
        .v3-link:hover::after { transform: scaleX(1); }
        .v3-top nav { display: flex; justify-content: center; flex-wrap: wrap; gap: 22px; font-size: 12px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: #fff; }
        .v3-fit.is-desktop .v3-top nav { font-size: 15px; gap: 34px; }
        .v3-top-actions { display: flex; align-items: center; gap: 16px; }
        .v3-lang, .v3-top-cta, .v3-cta, .v3-call .v3-ring > button, .v3-times button { border: 0; cursor: pointer; font-family: inherit; }
        .v3-lang { display: inline-flex; align-items: center; gap: 8px; background: none; color: #fff; padding: 0; font-size: 14px; font-weight: 600; }
        .v3-fit.is-desktop .v3-lang { font-size: 16px; }
        .v3-lang svg { width: 18px; height: 18px; fill: none; stroke: #fff; stroke-width: 1.6; }
        .v3-top-cta, .v3-cta, .v3-call .v3-ring > button { background: #fff; color: #111; border-radius: 999px; font-weight: 700; }
        .v3-top-cta { padding: 10px 16px; font-size: 14px; line-height: 1; box-shadow: 0 8px 24px rgba(0,0,0,0.18); }
        .v3-fit.is-desktop .v3-top-cta { padding: 12px 20px; font-size: 16px; }
        .v3-stage { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: 28px; padding-top: 18px; }
        .v3-fit.is-desktop .v3-stage { display: grid; grid-template-columns: minmax(0, 1fr) minmax(420px, 36vw); column-gap: 28px; align-items: stretch; position: relative; padding-top: 8px; height: auto; }
        .v3-copy { display: flex; flex-direction: column; justify-content: space-between; min-height: 0; max-width: 860px; }
        .v3-fit.is-desktop .v3-copy { width: auto; max-width: 760px; height: 100%; padding-bottom: 8px; }
        .v3 h1 { margin: 0; font-family: "Inter Tight", Inter, sans-serif; font-weight: 900; font-size: 52px; line-height: 0.86; letter-spacing: -0.045em; white-space: pre-line; color: #111; }
        .v3-fit.is-desktop .v3 h1 { font-size: clamp(64px, 6vw, 96px); }
        .v3-lead { max-width: 34em; margin: 14px 0 0; font-size: 17px; line-height: 1.35; font-weight: 500; color: #1a1a1a; }
        .v3-fit.is-desktop .v3-lead { font-size: 18px; margin-top: 12px; max-width: 520px; }
        .v3-punch { max-width: 34em; margin: 8px 0 0; font-size: 17px; line-height: 1.35; font-weight: 500; color: #1a1a1a; }
        .v3-fit.is-desktop .v3-punch { font-size: 18px; max-width: 520px; }
        .v3-punch strong { font-weight: 800; }
        .v3-points { list-style: none; margin: 20px 0 0; padding: 0; display: flex; flex-direction: column; gap: 12px; max-width: 430px; background: transparent; border: 0; box-shadow: none; }
        .v3-fit.is-desktop .v3-points { margin-top: 20px; gap: 12px; max-width: 430px; }
        .v3-points li { display: grid; grid-template-columns: 48px 1fr; column-gap: 14px; align-items: center; background: transparent; }
        .v3-ico { width: 48px; height: 48px; flex: none; border-radius: 15px; background: linear-gradient(180deg, #fff, #f4eee6); border: 1px solid rgba(255,255,255,0.95); box-shadow: 0 10px 22px rgba(48,28,8,0.16), inset 0 -1px 0 rgba(48,28,8,0.06); display: flex; align-items: center; justify-content: center; line-height: 0; transition: transform 0.2s ease; }
        .v3-ico svg { width: 24px; height: 24px; display: block; flex: none; fill: none; color: #111; stroke: currentColor; stroke-width: 1.9; stroke-linecap: round; stroke-linejoin: round; }
        @media (hover: hover) {
          .v3-points li:hover .v3-ico { transform: translateY(-1px); background: #fff; }
        }
        .v3-points strong { display: block; font-size: 16px; font-weight: 700; line-height: 1.1; letter-spacing: -0.015em; color: #111; }
        .v3-points li > div > span { display: block; margin-top: 2px; font-size: 13px; font-weight: 450; line-height: 1.25; color: rgba(17,17,17,0.68); }
        .v3-bottom { margin-top: 24px; }
        .v3-social { display: flex; align-items: center; gap: 12px; }
        .v3-faces { display: flex; }
        .v3-faces img { width: 42px; height: 42px; margin-left: -10px; border-radius: 50%; border: 2px solid #fff; object-fit: cover; display: block; }
        .v3-fit.is-desktop .v3-faces img { width: 48px; height: 48px; }
        .v3-faces img:first-child { margin-left: 0; }
        .v3-social b { color: #f0b429; letter-spacing: 2px; font-size: 14px; }
        .v3-social strong { display: block; margin-top: 2px; font-size: 18px; color: #fff; }
        .v3-fit.is-desktop .v3-social strong { font-size: 20px; }
        .v3-social span { display: block; max-width: 34ch; font-size: 13px; color: rgba(255,255,255,0.88); }
        .v3-cta { width: 100%; height: 62px; padding: 0 24px; font-size: 18px; box-shadow: 0 10px 30px rgba(0,0,0,0.22); }
        .v3-ring-wide { margin-top: 16px; width: min(100%, 420px); }
        .v3-fit.is-desktop .v3-ring-wide { width: 420px; }
        .v3-fit.is-desktop .v3-cta { height: 62px; font-size: 18px; }
        .v3-trust { display: flex; flex-wrap: wrap; gap: 16px; list-style: none; margin: 10px 0 0; padding: 0; font-size: 12px; color: rgba(255,255,255,0.88); }
        .v3-trust li { display: flex; align-items: center; gap: 6px; }
        .v3-pay { flex-wrap: wrap; row-gap: 6px; }
        .v3-pay-marks { display: inline-flex; align-items: center; gap: 4px; }
        .v3-pay-marks > span, .v3-pay-marks > a { display: inline-flex; align-items: center; justify-content: center; height: 16px; padding: 0 4px; border-radius: 3px; background: #fff; color: #111; font-size: 8px; font-weight: 800; letter-spacing: 0.04em; line-height: 1; text-decoration: none; }
        .v3-pay-apple { gap: 2px; }
        .v3-pay-marks .v3-pay-apple svg { width: 9px; height: 10px; fill: #111; stroke: none; }
        .v3-pay-mc { gap: 0; width: 22px; padding: 0; background: transparent; }
        .v3-pay-mc i { width: 10px; height: 10px; border-radius: 50%; background: #fff; box-shadow: inset 0 0 0 1px rgba(17,17,17,0.35); }
        .v3-pay-mc i + i { margin-left: -4px; }
        .v3-trust svg { width: 14px; height: 14px; fill: none; stroke: #fff; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
        .v3-pack, .v3-pack-note { display: none; }
        .v3-side { width: 100%; display: flex; flex-direction: column; gap: 14px; }
        .v3-fit.is-desktop .v3-side { position: relative; top: auto; right: auto; width: auto; height: 100%; justify-content: stretch; gap: 12px; }
        .v3-fit.is-desktop .v3-call,
        .v3-fit.is-desktop .v3-price { flex: none; }
        .v3-fit.is-desktop .v3-prog { flex: 1 1 0; min-height: 0; }
        .v3-card { background: linear-gradient(135deg, rgba(12,12,14,0.94), rgba(20,18,18,0.88)); color: #fff; border-radius: 22px; padding: 16px 18px; border: 1px solid rgba(255,255,255,0.14); box-shadow: 0 20px 60px rgba(0,0,0,0.18); backdrop-filter: blur(18px); }
        .v3-card h2 { margin: 0; font-family: "Inter Tight", Inter, sans-serif; font-weight: 800; font-size: 24px; line-height: 1.02; letter-spacing: -0.03em; white-space: pre-line; }
        .v3-fit.is-desktop .v3-card h2 { font-size: 26px; }
        .v3-card p { margin: 8px 0 0; font-size: 14px; line-height: 1.35; color: rgba(255,255,255,0.75); }
        .v3-call { display: grid; grid-template-columns: 1fr 168px; gap: 16px; align-items: center; }
        .v3-live { margin: 0 0 6px; font-size: 12px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: #fff; }
        .v3-live::before { content: ""; display: inline-block; width: 8px; height: 8px; margin-right: 7px; border-radius: 50%; background: #ff3b30; box-shadow: 0 0 0 4px rgba(255,59,48,0.2); }
        .v3-call .v3-ring > button { margin-top: 0; padding: 10px 14px; font-size: 14px; }
        .v3-fit.is-desktop .v3-pitch { display: none; }
        .v3-slot { margin-top: 8px; }
        .v3-slot-kicker { margin: 0; font-size: 11px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: #ffb199; }
        .v3-slot-day { margin: 3px 0 0; font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.82); }
        .v3-times { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
        .v3-times button { background: rgba(255,255,255,0.12); color: #fff; border: 1px solid rgba(255,255,255,0.28); border-radius: 999px; padding: 6px 10px; font-size: 13px; font-weight: 700; line-height: 1; }
        .v3-times button.is-on { background: #fff; color: #111; border-color: #fff; }
        .v3-cal { position: relative; margin-top: 8px; }
        .v3-cal-open { border: 0; background: none; padding: 0; color: rgba(255,255,255,0.86); font: inherit; font-size: 12px; font-weight: 650; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
        .v3-call.is-open { z-index: 40; }
        .v3-cal-pop { position: absolute; left: 0; top: 22px; z-index: 40; width: 248px; padding: 12px; border-radius: 16px; background: #161616; color: #fff; border: 1px solid rgba(255,255,255,0.16); box-shadow: 0 22px 50px rgba(0,0,0,0.55); }
        .v3-cal-nav { display: flex; align-items: center; justify-content: space-between; }
        .v3-cal-nav strong { font-size: 13px; font-weight: 700; text-transform: capitalize; }
        .v3-cal-nav button, .v3-cal-grid button, .v3-cal-hours button { font-family: inherit; cursor: pointer; }
        .v3-cal-nav button { border: 0; background: none; color: #fff; font-size: 18px; line-height: 1; }
        .v3-cal-nav button:disabled { opacity: 0.3; cursor: default; }
        .v3-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; margin-top: 8px; text-align: center; }
        .v3-cal-grid span, .v3-cal-grid i { height: 26px; font-size: 10px; font-weight: 700; color: rgba(255,255,255,0.45); display: grid; place-items: center; }
        .v3-cal-grid button { height: 26px; border: 0; border-radius: 8px; background: transparent; color: #fff; font-size: 12px; font-weight: 650; }
        .v3-cal-grid button:disabled { color: rgba(255,255,255,0.22); cursor: default; }
        .v3-cal-grid button.is-on { background: #fff; color: #111; }
        .v3-cal-hours p { margin: 8px 0 6px; font-size: 11px; color: rgba(255,255,255,0.7); }
        .v3-cal-hours div { display: flex; gap: 6px; }
        .v3-cal-hours button { border: 0; border-radius: 999px; background: #fff; color: #111; font-size: 12px; font-weight: 700; padding: 6px 8px; }
        .v3-portrait { position: relative; border-radius: 14px; overflow: hidden; height: 132px; background: #111; }
        .v3-fit.is-desktop .v3-portrait { height: auto; min-height: 176px; align-self: stretch; }
        .v3-portrait img { width: 100%; height: 100%; object-fit: cover; object-position: center 20%; }
        .v3-call-ui { position: absolute; left: 8px; right: 8px; bottom: 8px; display: flex; justify-content: center; gap: 8px; }
        .v3-call-ui svg { width: 28px; height: 28px; padding: 6px; border-radius: 50%; background: #fff; fill: #111; box-sizing: border-box; }
        .v3-call-ui svg:last-child { background: #ff3b30; fill: #fff; }
        .v3-prog-head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
        .v3-prog-head .v3-link { font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.8); white-space: nowrap; letter-spacing: 0; text-transform: none; }
        .v3-thumbs { list-style: none; margin: 12px 0 0; padding: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
        .v3-fit.is-desktop .v3-prog { display: flex; flex-direction: column; }
        .v3-fit.is-desktop .v3-prog > p { display: none; }
        .v3-fit.is-desktop .v3-thumbs { flex: 1; min-height: 0; grid-template-rows: repeat(2, minmax(0, 1fr)); gap: 10px; }
        .v3-thumbs li { position: relative; border-radius: 14px; overflow: hidden; aspect-ratio: 16 / 10; background: #222; min-width: 0; }
        .v3-fit.is-desktop .v3-thumbs li { aspect-ratio: auto; height: 100%; min-height: 0; }
        .v3-thumbs img, .v3-thumbs video { width: 100%; height: 100%; object-fit: cover; object-position: center 35%; display: block; transition: transform 0.4s ease; }
        .v3-thumbs li:hover img, .v3-thumbs li:hover video { transform: scale(1.05); }
        .v3-thumbs b { position: absolute; left: 0; right: 30px; bottom: 0; margin: 0; padding: 18px 8px 8px; background: linear-gradient(transparent, rgba(0,0,0,0.78)); font-size: 11px; font-weight: 700; letter-spacing: 0.05em; line-height: 1.15; text-align: left; text-transform: uppercase; color: #fff; z-index: 2; }
        .v3-mosaic { position: absolute; inset: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); grid-template-rows: 1fr; gap: 3px; }
        .v3-mosaic > span { position: relative; overflow: hidden; min-width: 0; min-height: 0; }
        .v3-mosaic em { position: absolute; left: 4px; top: 3px; z-index: 1; font-style: normal; font-size: 8px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: #fff; text-shadow: 0 1px 4px rgba(0,0,0,0.75); }
        .v3-thumb-mosaic b { right: 28px; padding: 22px 8px 8px; font-size: 13px; line-height: 1.15; text-transform: none; letter-spacing: -0.02em; background: linear-gradient(transparent, rgba(0,0,0,0.88) 42%); }
        .v3-thumb-secret img { filter: blur(5px); transform: scale(1.14); }
        .v3-thumbs li.v3-thumb-secret:hover img { transform: scale(1.18); }
        .v3-thumb-go { position: absolute; right: 7px; bottom: 7px; width: 22px; height: 22px; border-radius: 50%; background: rgba(255,255,255,0.16); border: 1px solid rgba(255,255,255,0.4); display: grid; place-items: center; }
        .v3-thumb-go svg { width: 11px; height: 11px; fill: none; stroke: #fff; stroke-width: 2.4; stroke-linecap: round; stroke-linejoin: round; }
        .v3-offer { display: flex; align-items: center; gap: 8px 10px; margin: 0 0 12px; }
        .v3-launch { background: rgba(255,70,60,0.12); color: #ff554d; border: 1px solid rgba(255,70,60,0.3); text-transform: uppercase; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; border-radius: 6px; padding: 5px 8px; white-space: nowrap; }
        .v3-spots { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #fff; white-space: nowrap; }
        .v3-spots svg { width: 13px; height: 13px; fill: none; stroke: #ff554d; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
        .v3-meter { width: 110px; height: 5px; margin-left: auto; flex: none; border-radius: 999px; background: rgba(255,255,255,0.12); overflow: hidden; }
        .v3-meter i { display: block; height: 100%; border-radius: inherit; background: #ff6259; transition: width 700ms ease; }
        .v3-price-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
        .v3-price-line { display: flex; align-items: baseline; flex-wrap: nowrap; gap: 6px; }
        .v3-price-line em { font-size: 21px; font-weight: 500; }
        .v3-price-line s { font-size: 19px; font-weight: 500; text-decoration: line-through; opacity: 0.45; color: #fff; }
        .v3-price-line b { align-self: center; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; padding: 4px 7px; border-radius: 6px; background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.82); border: 1px solid rgba(255,255,255,0.18); }
        .v3-price-top p { margin: 0; letter-spacing: 0.18em; font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.7); }
        .v3-price-top strong { font-family: "Inter Tight", Inter, sans-serif; font-weight: 900; font-size: 64px; letter-spacing: -0.05em; line-height: 0.85; color: #fff; }
        .v3-fit.is-desktop .v3-price-top strong { font-size: 72px; font-weight: 900; line-height: 0.9; }
        .v3-fit.is-desktop .v3-price { display: flex; flex-direction: column; justify-content: center; }
        .v3-price-top em { font-style: normal; font-size: 22px; font-weight: 500; color: rgba(255,255,255,0.75); margin-left: 4px; }
        .v3-cancel { display: flex; align-items: center; gap: 10px; max-width: 16ch; font-size: 14px; font-weight: 600; color: #fff; line-height: 1.15; white-space: pre-line; }
        .v3-cancel i { width: 36px; height: 36px; flex: none; border: 1.5px solid rgba(255,255,255,0.9); border-radius: 50%; display: grid; place-items: center; }
        .v3-cancel svg { width: 16px; height: 16px; fill: none; stroke: #fff; stroke-width: 1.7; stroke-linejoin: round; stroke-linecap: round; }
        .v3-price ul { list-style: none; margin: 12px 0 0; padding: 0; display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: repeat(4, auto); grid-auto-flow: column; gap: 6px 16px; align-items: start; }
        .v3-price li { font-size: 13px; line-height: 1.3; }
        .v3-price li::before { content: "✓  "; }
        .v3-aim { position: relative; cursor: pointer; }
        .v3-aim::before { content: none; }
        .v3-ring { position: relative; display: inline-flex; padding: 0; border-radius: 999px; overflow: hidden; isolation: isolate; vertical-align: middle; background: transparent; }
        .v3-call .v3-ring { padding: 4px; background: #111; }
        .v3-ring::before { content: none; }
        .v3-call .v3-ring::before { content: ""; position: absolute; left: 50%; top: 50%; width: 220%; aspect-ratio: 1; background: conic-gradient(transparent 0 200deg, #fff 250deg, #fff 290deg, transparent 340deg); animation: v3-spin 1.4s linear infinite; z-index: 0; }
        .v3-ring > button { position: relative; z-index: 1; box-shadow: none; }
        .v3-price-cta { display: none; }
        .v3-call .v3-ring { margin-top: 12px; }
        .v3-call .v3-ring > button { margin-top: 0; }
        @keyframes v3-spin { from { transform: translate(-50%, -50%) rotate(0deg); } to { transform: translate(-50%, -50%) rotate(360deg); } }
        @property --v3-angle { syntax: "<angle>"; inherits: false; initial-value: 0deg; }
        @keyframes v3-orbit { to { --v3-angle: 360deg; } }
        @media (min-width: 1100px) and (max-height: 940px) {
          .v3-fit.is-desktop .v3 { padding-top: 24px; padding-bottom: 24px; }
          .v3-fit.is-desktop .v3 h1 { font-size: clamp(56px, 9.4vh, 96px); }
          .v3-fit.is-desktop .v3-lead { font-size: 18px; margin-top: 12px; }
          .v3-fit.is-desktop .v3-points { margin-top: 14px; gap: 8px; }
          .v3-fit.is-desktop .v3-points li { grid-template-columns: 44px 1fr; }
          .v3-fit.is-desktop .v3-ico { width: 44px; height: 44px; border-radius: 14px; }
          .v3-fit.is-desktop .v3-ico svg { width: 22px; height: 22px; }
          .v3-fit.is-desktop .v3-faces img { width: 42px; height: 42px; }
          .v3-fit.is-desktop .v3-ring-wide { margin-top: 12px; }
          .v3-fit.is-desktop .v3-cta { height: 54px; }
          .v3-fit.is-desktop .v3-side { gap: 10px; }
          .v3-fit.is-desktop .v3-card { padding: 14px 16px; }
          .v3-fit.is-desktop .v3-card h2 { font-size: 23px; }
          .v3-fit.is-desktop .v3-call { grid-template-columns: 1fr 132px; }
          .v3-fit.is-desktop .v3-slot { margin-top: 6px; }
          .v3-fit.is-desktop .v3-times { margin-top: 6px; }
          .v3-fit.is-desktop .v3-times button { padding: 5px 8px; font-size: 12px; }
          .v3-fit.is-desktop .v3-portrait { min-height: 132px; }
          .v3-fit.is-desktop .v3-thumbs { margin-top: 10px; gap: 8px; }
          .v3-fit.is-desktop .v3-offer { margin-bottom: 8px; }
          .v3-fit.is-desktop .v3-price-top strong { font-size: 56px; }
          .v3-fit.is-desktop .v3-price ul { margin-top: 8px; gap: 3px 16px; }
          .v3-fit.is-desktop .v3-price li { font-size: 12px; }
        }
        @media (max-width: 1099px) {
          .v3-fit { position: relative; height: auto; min-height: 100dvh; overflow: auto; -webkit-overflow-scrolling: touch; }
          .v3 { height: auto; min-height: 100dvh; overflow: visible; padding: 16px 16px calc(28px + env(safe-area-inset-bottom)); background-position: 68% 18%; }
          .v3-price-cta { display: block; width: 100%; min-height: 48px; margin-top: 14px; border: 0; border-radius: 999px; background: #fff; color: #111; font-family: inherit; font-weight: 700; font-size: 16px; cursor: pointer; }
          .v3-price-cta:disabled { cursor: wait; }
          .v3-top .v3-ring { padding: 0; background: transparent; }
          .v3-top-cta { white-space: nowrap; }
          .v3-copy { display: none; }
          .v3-pack { display: flex; flex-direction: column; gap: 4px; margin: 0; padding: 14px 16px; border-radius: 18px; background: rgba(12,12,14,0.9); color: #fff; border: 1px solid rgba(255,255,255,0.14); }
          .v3-pack strong { font-size: 17px; font-weight: 800; letter-spacing: -0.03em; line-height: 1.15; }
          .v3-pack span { font-size: 14px; line-height: 1.35; color: rgba(255,255,255,0.78); }
          .v3-pack-note { display: block; margin: 0 0 6px; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: rgba(255,255,255,0.62); }
          .v3-top-actions .v3-ring { display: none; }
          .v3-top { gap: 10px; }
          .v3-top nav { display: none; }
          .v3-stage { padding-top: 8px; gap: 14px; }
          .v3 h1 { font-size: 44px; }
          .v3-lead, .v3-points { max-width: none; }
          .v3-copy { max-width: none; }
          .v3-ring-wide, .v3-fit.is-desktop .v3-ring-wide { width: 100%; }
          .v3-call { grid-template-columns: 1fr 112px; }
          .v3-portrait, .v3-fit.is-desktop .v3-portrait { height: 128px; min-height: 0; }
          .v3-thumbs { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .v3-thumbs li, .v3-fit.is-desktop .v3-thumbs li { aspect-ratio: 16 / 10; height: auto; min-height: 0; }
          .v3-price ul { grid-auto-flow: row; grid-template-rows: none; grid-template-columns: 1fr; }
          .v3-price-top strong, .v3-fit.is-desktop .v3-price-top strong { font-size: 56px; }
          .v3-card h2 { font-size: 22px; }
          .v3-social { align-items: flex-start; }
          .v3-social strong, .v3-social span, .v3-trust { color: #111; }
          .v3-offer { flex-wrap: wrap; }
          .v3-meter { margin-left: 0; }
          .v3-trust svg { stroke: #111; }
        }
        @media (max-width: 420px) {
          .v3 h1 { font-size: 38px; }
          .v3-top-cta { padding: 10px 12px; font-size: 13px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .v3-aim::before, .v3-ring::before { animation: none; }
          .v3-meter i { transition: none; }
        }
        .v3-pay-marks > button.v3-pay-sol { display: inline-flex; align-items: center; gap: 5px; height: 22px; margin-left: 2px; padding: 0 8px 0 6px; border: 0; border-radius: 999px; background: #111; color: #fff; font-family: inherit; font-size: 11px; font-weight: 700; letter-spacing: 0; line-height: 1; cursor: pointer; }
        .v3-pay-sol .v3-sol-mark, .v3-sol-kicker .v3-sol-mark { width: 12px; height: 10px; display: block; flex: none; fill: none; stroke: none; }
        .v3-pay-sol .v3-sol-mark path, .v3-sol-kicker .v3-sol-mark path { stroke: none; }
        .v3-sol-mobile { display: none; }
        .v3-sol-sheet { position: fixed; inset: 0; z-index: 80; display: flex; align-items: center; justify-content: center; padding: 20px; background: rgba(8,8,10,0.62); }
        .v3-sol-card { position: relative; width: min(100%, 360px); padding: 22px 22px 18px; border-radius: 22px; background: #111; color: #fff; text-align: center; box-shadow: 0 24px 80px rgba(0,0,0,0.35); }
        .v3-sol-x { position: absolute; top: 8px; right: 12px; border: 0; background: transparent; color: #fff; font-size: 24px; line-height: 1; cursor: pointer; }
        .v3-sol-kicker { display: flex; align-items: center; justify-content: center; gap: 6px; margin: 0; font-size: 13px; font-weight: 700; }
        .v3-sol-card h2 { margin: 8px 0 0; font-family: "Inter Tight", Inter, sans-serif; font-weight: 800; font-size: 22px; line-height: 1.1; letter-spacing: -0.03em; color: #fff; white-space: normal; }
        .v3-sol-card > strong { display: block; margin-top: 8px; font-size: 40px; line-height: 1; letter-spacing: -0.04em; }
        .v3-sol-card > p { margin: 6px 0 0; color: rgba(255,255,255,0.72); font-size: 14px; line-height: 1.35; }
        .v3-sol-go { display: flex; align-items: center; justify-content: center; height: 48px; margin-top: 16px; border-radius: 999px; background: #fff; color: #111; font-weight: 700; text-decoration: none; }
        .v3-sol-card img { width: 168px; height: 168px; margin: 14px auto 0; border-radius: 12px; background: #fff; display: block; }
        .v3-sol-scan { display: block; margin-top: 8px; font-size: 12px; color: rgba(255,255,255,0.62); }
        .v3-sol-addr { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; margin-top: 12px; padding: 10px 12px; border: 1px solid rgba(255,255,255,0.16); border-radius: 12px; background: transparent; color: #fff; font: inherit; font-size: 13px; cursor: pointer; }
        .v3-sol-addr em { font-style: normal; font-weight: 700; color: #14F195; }
        @media (max-width: 1099px) {
          .v3-sol-mobile { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 48px; margin-top: 8px; border: 0; border-radius: 999px; background: #fff; color: #111; font-family: inherit; font-weight: 700; font-size: 16px; cursor: pointer; }
          .v3-sol-mobile .v3-sol-mark { width: 16px; height: 13px; display: block; flex: none; stroke: none; }
        }
      `}</style>
    </div>
  );
}
